import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { fmtEUR } from "@/lib/format";

/* Palettes ID Park — jamais cyclées, jamais de double axe Y */
export const RAMPE_AGE = ["#FCDFD6", "#FCC4B5", "#FBA790", "#F9896B", "#E05A33", "#B8451F"];
export const RAMPE_PRIX = ["#0E7490", "#4E9DB0", "#CBD2DE", "#D97706", "#B8451F", "#B91C1C"];
export const MIX_DEFICIT = "#E05A33";
export const MIX_SURSTOCK = "#2F6FED";
export const MIX_NEUTRE = "#D6DCE6";
export const SERIES_CATEGORIELLES = ["#2F6FED", "#E8853D", "#16A34A", "#7C5CE0", "#DC2626"];

const QUARTILE_STYLES = {
  Q1: { bg: "#DCFCE7", fg: "#15803D" },
  Q2: { bg: "#EAF1FE", fg: "#2F6FED" },
  Q3: { bg: "#FEF3C7", fg: "#92400E" },
  Q4: { bg: "#FEE2E2", fg: "#B91C1C" },
};

export const fmtNum = (v, suffixe = "") => {
  if (v === null || v === undefined) return "—";
  return `${String(v).replace(".", ",")}${suffixe}`;
};

export const fmtValeur = (v, unit) => {
  if (v === null || v === undefined) return "—";
  if (unit === "eur") return fmtEUR(v);
  if (unit === "pct") return fmtNum(v, " %");
  if (unit === "days") return fmtNum(v, " j");
  if (unit === "hours") return fmtNum(v, " h");
  return fmtNum(v);
};

export const fmtEcart = (v) => {
  if (v === null || v === undefined) return "—";
  const s = String(Math.abs(v)).replace(".", ",");
  return `${v >= 0 ? "+" : "−"}${s} %`;
};

export function QuartilePill({ quartile, partiel, testId }) {
  if (partiel) {
    return (
      <span className="pill" style={{ background: "#EEF1F6", color: "#64748B" }} data-testid={testId}>
        Score partiel
      </span>
    );
  }
  if (!quartile) return <span className="text-[--idc-muted]">—</span>;
  const c = QUARTILE_STYLES[quartile] || QUARTILE_STYLES.Q4;
  return (
    <span className="pill" style={{ background: c.bg, color: c.fg }} data-testid={testId}>
      {quartile}
    </span>
  );
}

const STATUT_STYLES = {
  ok: { bg: "#DCFCE7", fg: "#15803D", label: "OK" },
  warning: { bg: "#FEF3C7", fg: "#92400E", label: "À surveiller" },
  alerte: { bg: "#FEE2E2", fg: "#B91C1C", label: "Alerte" },
  gris: { bg: "#EEF1F6", fg: "#64748B", label: "—" },
};

export function StatutPastille({ statut, valeur }) {
  const c = STATUT_STYLES[statut] || STATUT_STYLES.gris;
  return (
    <span className="pill" style={{ background: c.bg, color: c.fg }}>
      {valeur ?? c.label}
    </span>
  );
}

export function NiveauPill({ niveau }) {
  if (!niveau) return <span className="text-[--idc-muted]">—</span>;
  return (
    <span className="pill p-park" title="Niveau d'héritage de la cible">{niveau}</span>
  );
}

export function ParkBadge() {
  return (
    <span className="pill p-park font-bold tracking-wide" data-testid="park-badge">ID Park</span>
  );
}

export function NoteBar({ note }) {
  if (note === null || note === undefined) {
    return <span className="text-[--idc-muted] text-[12px]">—</span>;
  }
  const couleur = note >= 70 ? "#16A34A" : note >= 40 ? "#D97706" : "#B91C1C";
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="flex-1 h-2 rounded-full bg-[#EEF1F6] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${note}%`, background: couleur }} />
      </div>
      <span className="text-[12px] font-bold tabular-nums w-9 text-right">{fmtNum(note)}</span>
    </div>
  );
}

/* Pastille de score dans le header — quartile + score du périmètre courant */
export function ScoreHeaderPastille() {
  const navigate = useNavigate();
  const { auth, peutVoir } = useAuth();
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!peutVoir("stock_vehicules")) return;
    let actif = true;
    api.get("/score/scope")
      .then((r) => {
        if (!actif) return;
        const sites = r.data?.sites || [];
        const mien = sites.find((s) => s.dealer_id === auth?.user?.site) || sites[0];
        if (mien) setInfo(mien);
      })
      .catch(() => {});
    return () => { actif = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!info || info.score === null) return null;
  const c = info.partiel ? { bg: "#EEF1F6", fg: "#64748B" } : (QUARTILE_STYLES[info.quartile] || QUARTILE_STYLES.Q4);
  return (
    <button
      onClick={() => navigate("/pilote-stock?onglet=score")}
      className="hidden sm:flex items-center gap-1.5 pill hover:opacity-80 transition-opacity"
      style={{ background: c.bg, color: c.fg }}
      title={`Score VO — ${info.dealer_id}`}
      data-testid="header-score-pastille"
    >
      <span className="font-bold">{info.partiel ? "Partiel" : info.quartile}</span>
      <span className="tabular-nums font-extrabold">{fmtNum(info.score)}</span>
    </button>
  );
}
